import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MagnifyingGlass, UserPlus, ArrowRight, Users, UploadSimple } from "@phosphor-icons/react";
import StatusBadge from "@/components/StatusBadge";
import AddStaffDialog from "@/components/AddStaffDialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const ROLE_LABELS = { coach: "Coach", trainer: "Trainer", referee: "Referee", volunteer: "Volunteer" };

export default function StaffPage() {
  const [staff, setStaff] = useState([]);
  const [q, setQ] = useState("");
  const [role, setRole] = useState("all");
  const [status, setStatus] = useState("all");
  const [addOpen, setAddOpen] = useState(false);

  const load = async () => {
    const { data } = await api.get("/staff-with-status");
    setStaff(data);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return staff.filter((s) => {
      if (role !== "all" && s.role !== role) return false;
      if (status !== "all" && s.overall_status !== status) return false;
      if (q && !(s.name.toLowerCase().includes(q.toLowerCase()) || (s.team || "").toLowerCase().includes(q.toLowerCase()))) return false;
      return true;
    });
  }, [staff, q, role, status]);

  return (
    <div className="p-4 md:p-8 max-w-[1600px] mx-auto">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <div className="font-label text-orange-400 mb-2">ROSTER</div>
          <h1 className="text-4xl md:text-5xl font-display font-black tracking-tight">Staff & Volunteers</h1>
          <p className="text-slate-400 mt-2">{staff.length} members · {filtered.length} shown</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Link to="/import">
            <Button
              data-testid="goto-import-button"
              variant="outline"
              className="border-white/10 bg-slate-900 text-slate-100 hover:bg-slate-800 rounded-full h-11 px-4"
            >
              <UploadSimple size={16} weight="bold" className="mr-2" /> Import CSV
            </Button>
          </Link>
          <Button
            data-testid="add-staff-button"
            onClick={() => setAddOpen(true)}
            className="bg-orange-500 text-white hover:bg-orange-400 font-semibold rounded-full h-11 px-5"
          >
            <UserPlus size={16} weight="bold" className="mr-2" /> Add Staff
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-[1fr_180px_180px] gap-3 mb-6">
        <div className="relative">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input
            data-testid="staff-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name or team…"
            className="pl-10 bg-slate-900 border-white/10 focus:border-orange-500 h-11"
          />
        </div>
        <Select value={role} onValueChange={setRole}>
          <SelectTrigger data-testid="role-filter" className="bg-slate-900 border-white/10 h-11"><SelectValue placeholder="Role" /></SelectTrigger>
          <SelectContent className="bg-slate-900 border-white/10 text-slate-100">
            <SelectItem value="all">All Roles</SelectItem>
            <SelectItem value="coach">Coaches</SelectItem>
            <SelectItem value="trainer">Trainers</SelectItem>
            <SelectItem value="referee">Referees</SelectItem>
            <SelectItem value="volunteer">Volunteers</SelectItem>
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="status-filter" className="bg-slate-900 border-white/10 h-11"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent className="bg-slate-900 border-white/10 text-slate-100">
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="current">Current</SelectItem>
            <SelectItem value="expiring_soon">Expiring Soon</SelectItem>
            <SelectItem value="expired">Expired</SelectItem>
            <SelectItem value="none">No Credentials</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="rounded-2xl border border-white/10 bg-slate-900 overflow-hidden">
        {filtered.length === 0 ? (
          <div className="p-16 text-center">
            <Users size={44} weight="duotone" className="text-slate-600 mx-auto mb-3" />
            <div className="text-slate-300 font-semibold">No staff match your filters</div>
            <div className="text-slate-500 text-sm mt-1">Adjust the filters or add new staff.</div>
          </div>
        ) : (
          <>
            <div className="hidden lg:grid grid-cols-[1.5fr_1fr_1fr_1fr_1fr_60px] gap-3 font-label text-slate-500 px-6 py-3 border-b border-white/10">
              <div>NAME</div>
              <div>ROLE</div>
              <div>TEAM / DEPT</div>
              <div>CREDENTIALS</div>
              <div>STATUS</div>
              <div></div>
            </div>
            <div className="divide-y divide-white/5">
              {filtered.map((s) => (
                <Link
                  key={s.id}
                  to={`/staff/${s.id}`}
                  data-testid={`staff-row-${s.id}`}
                  className="grid grid-cols-2 lg:grid-cols-[1.5fr_1fr_1fr_1fr_1fr_60px] gap-3 px-6 py-4 hover:bg-white/[0.02] transition-colors items-center"
                >
                  <div className="flex items-center gap-3 col-span-2 lg:col-span-1 min-w-0">
                    <div className="w-9 h-9 rounded-full bg-slate-800 grid place-items-center text-xs font-bold text-slate-300 shrink-0">
                      {s.name.split(" ").map(n => n[0]).slice(0,2).join("")}
                    </div>
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-100 truncate">{s.name}</div>
                      <div className="text-xs text-slate-500 truncate">{s.email || "—"}</div>
                    </div>
                  </div>
                  <div className="text-sm text-slate-300 capitalize">{ROLE_LABELS[s.role] || s.role}</div>
                  <div className="text-sm text-slate-400">{s.team || "—"}</div>
                  <div className="text-sm text-slate-300 font-semibold">{s.credential_count}</div>
                  <div><StatusBadge status={s.overall_status} color={s.status_color} testId={`status-${s.id}`} /></div>
                  <div className="hidden lg:flex justify-end text-slate-500">
                    <ArrowRight size={16} weight="bold" />
                  </div>
                </Link>
              ))}
            </div>
          </>
        )}
      </div>

      <AddStaffDialog open={addOpen} onOpenChange={setAddOpen} onCreated={load} />
    </div>
  );
}
